import type {CodeNavigationInfo, DefinitionOrReference} from '@github-ui/code-nav'
import {LatestCommitSingleLine} from '@github-ui/code-view-shared/components/LatestCommit'
import {useOpenPanel, useSetOpenPanel} from '@github-ui/code-view-shared/contexts/OpenPanelContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import type {Blame} from '@github-ui/code-view-types'
import type {CopilotInfo} from '@github-ui/copilot-common-types'
import {CopilotPopover} from '@github-ui/copilot-popover'
import type {BannerProps} from '@github-ui/copilot-spaces/components/SpacesSelectionMenu'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import safeStorage from '@github-ui/safe-storage'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, useScreenSize} from '@github-ui/screen-size'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {useInFileSearchResults} from '@github-ui/use-in-file-search-results'
import {Banner} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {useFindInFileOpen} from '../contexts/FindInFileOpenContext'
import {useSearchTerm, useSetSearchTerm} from '../contexts/SearchTermContext'
import {useCurrentBlame} from '../hooks/CurrentBlame'
import type {CurrentBlob} from '../hooks/CurrentBlob'
import {CurrentLineRefMapProvider} from '../hooks/CurrentLineRefMap'
import {useCurrentStyledBlob} from '../hooks/CurrentStyledBlob'
import {useCodeNavigation} from '../hooks/use-code-navigation'
import {focusSymbolSearch, useFocusSymbolPane} from '../hooks/use-focus-symbol-pane'
import {stickyHeaderId} from '../hooks/use-sticky-header-height'
import {DELETE_STICKY_LINES_VALUE, useStickyLines} from '../hooks/use-sticky-lines'
import {useStickyHeaderStyle} from '../hooks/use-sticky-observer'
import {updateExpandPreferences} from '../hooks/use-update-panel-expand-preferences'
import BlobContent from './blob/BlobContent'
import type {CodeLinesHandle} from './blob/BlobContent/Code/code-lines-handle'
import styles from './BlobViewContent.module.css'
import BlobViewHeader from './headers/BlobViewHeader'
import CodeSizeDetails from './headers/header-components/CodeSizeDetails'
import {PanelContent} from './PanelContent'

// Stable empty reference so the derived symbol highlight keeps an identical identity when nothing is selected.
const EMPTY_SEARCH_RESULTS: DefinitionOrReference[] = []

const safeLocalStorage = safeStorage('localStorage')

export function BlobViewContent({
  blame,
  blob,
  symbolsExpanded,
  setValidCodeNav,
  showTree,
  treeToggleElement,
  validCodeNav,
  copilotInfo,
}: {
  blame?: Blame
  blob: CurrentBlob
  symbolsExpanded: boolean
  setValidCodeNav: (show: boolean) => void
  showTree: boolean
  treeToggleElement: React.ReactElement
  validCodeNav: boolean
  copilotInfo?: CopilotInfo
}) {
  const {currentStickyLines, setStickyLines} = useStickyLines()
  const searchTerm = useSearchTerm()
  const setSearchTerm = useSetSearchTerm()
  const path = usePath()
  const symbolsEnabled = blob.symbolsEnabled
  const [stickyHeaderElement, stickyHeaderRef] = useState<Element | null>(null)
  const stickyStyle = useStickyHeaderStyle()

  const [autoFocusSearch, setAutoFocusSearch] = useState(false)
  const [bannerProps, setBannerProps] = useState<BannerProps | null>(null)
  const {openPanel, setOpenPanel} = useOpenPanel()
  const codeLinesHandleRef = useRef<CodeLinesHandle>(null)
  const shouldOpenPanel = useCodeViewOptions().openSymbolsOption.enabled && symbolsEnabled
  const {stylingDirectives, rawLines, colorizedLines} = useCurrentStyledBlob()

  // if narrow, the code symbols panel is a dialog instead of a panel
  const {screenSize} = useScreenSize()

  useFocusSymbolPane(focusSearch => {
    if (focusSearch) {
      setAutoFocusSearch(true)
    }
  })

  const {sendRepoClickEvent} = useReposAnalytics()

  const isFirstRenderRef = useRef(true)

  const onScrollEvent = useEffectEvent(() => {
    if (window.scrollY < 300) {
      //if the user scrolled to the top, we want to make sure all sticky lines are cleared out in that situation
      setStickyLines(DELETE_STICKY_LINES_VALUE, true)
    }
  })

  useEffect(() => {
    let ticking = false
    const handler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          onScrollEvent()
          ticking = false
        })
        ticking = true
      }
    }
    // rather than set up an intersection observer on a particular element, we only want to know if the user has scrolled
    // to the very top of the page. The element would never really intersect because it is sticky, so this is the only
    // realistic way for us to determine if the user has scrolled to the top of the page.
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', handler)
    return () => {
      window.removeEventListener('scroll', handler)
    }
  }, [])

  const hasBlame = !!useCurrentBlame()

  const openCodeNav = useCallback(() => {
    if (!hasBlame && shouldOpenPanel) {
      setOpenPanel('codeNav')
      sendRepoClickEvent('BLOB_SYMBOLS_MENU.OPEN_WITH_SYMBOL')
      // Defer localStorage write to avoid blocking interaction
      requestIdleCallback(() => safeLocalStorage.setItem('codeNavOpen', 'codeNav'))
      updateExpandPreferences(null, true, null)
    }
  }, [hasBlame, shouldOpenPanel, setOpenPanel, sendRepoClickEvent])

  const {isCodeNavLoading, codeNavInfo, showCodeNavWithSymbol, showCodeNavForToken, searchingText, setSearchingText} =
    useCodeNavigation(blob, openCodeNav, setValidCodeNav, ssrSafeLocation.hash, hasBlame)

  // Find-in-file matches come from the worker, keyed on the active search term.
  const {searchStatus, searchResults, focusedSearchResult, setFocusedSearchResult} = useInFileSearchResults(
    codeNavInfo,
    searchTerm,
  )

  const {findInFileOpen} = useFindInFileOpen()

  // The clicked-symbol highlight is a pure function of the current blob and the selected symbol, so we derive
  // it during render instead of pushing it up from the panel through an effect. `getReferencesToSymbol` returns
  // every in-file occurrence (definition + usages) — exactly the set the overlay highlights — synchronously, so
  // the clicked token is highlighted immediately and reliably regardless of how async code-nav defs/refs resolve.
  const symbolHighlight = useMemo(
    () =>
      codeNavInfo && searchingText.selectedText
        ? codeNavInfo.getReferencesToSymbol(searchingText.selectedText)
        : EMPTY_SEARCH_RESULTS,
    [codeNavInfo, searchingText.selectedText],
  )

  // Find-in-file owns the highlight overlay while it is actively searching; otherwise the selected symbol does.
  const findInFileActive = findInFileOpen && searchTerm.length > 0
  const overlaySearchResults = findInFileActive ? searchResults : symbolHighlight
  const overlayFocusedSearchResult = findInFileActive ? focusedSearchResult : undefined

  const onPathChange = useEffectEvent(() => {
    if (!isFirstRenderRef.current) {
      //remove sticky lines when navigating to a new file
      setStickyLines(DELETE_STICKY_LINES_VALUE, true)

      //reset panel when navigating to new file
      setSearchTerm('')

      // Don't autofocus search when navigating
      setAutoFocusSearch(false)

      setSearchingText({selectedText: '', lineNumber: -1, offset: 0})
    } else {
      isFirstRenderRef.current = false
      // If this is the first render, open the panel if we have text selected from the hash
      if (searchingText.selectedText && !openPanel && shouldOpenPanel && symbolsExpanded) {
        openCodeNav()
      }
    }
  })

  useEffect(() => {
    void path
    onPathChange()
  }, [path])

  const {
    headerInfo: {toc},
  } = blob

  // Latch: becomes true once we've rendered with no openPanel, and resets when codeNavInfo changes
  // (i.e. the user navigates to a new file). Tracked in state and updated during render — rather than a
  // mutated memo/ref — so the component stays optimizable by the React Compiler.
  const [latchedCodeNavInfo, setLatchedCodeNavInfo] = useState(codeNavInfo)
  const [hasRenderedWithNoOpenPanel, setHasRenderedWithNoOpenPanel] = useState(!openPanel)
  if (latchedCodeNavInfo !== codeNavInfo) {
    setLatchedCodeNavInfo(codeNavInfo)
    setHasRenderedWithNoOpenPanel(!openPanel)
  } else if (!openPanel && !hasRenderedWithNoOpenPanel) {
    setHasRenderedWithNoOpenPanel(true)
  }

  const showPanel =
    openPanel !== undefined &&
    codeNavInfo &&
    validCodeNav &&
    // we show the symbol panel by default, but if there are no symbols, we shouldn't open the symbol panel. if
    // hasRenderedWithNoOpenPanel is true, that implies the user has deliberately requested the symbols panel
    !(codeNavInfo.symbols.length === 0 && openPanel === 'codeNav' && !hasRenderedWithNoOpenPanel) &&
    // if we don't have a toc, don't show the toc
    !(!toc && openPanel === 'toc') &&
    !(openPanel === 'edit') &&
    // don't show the symbol panel if it's disabled
    !(!symbolsEnabled && openPanel === 'codeNav')
  const actualOpenPanel = showPanel ? openPanel : undefined

  // if we're not showing the panel, setOpenPanel to maintain consistency
  useEffect(() => {
    if (openPanel && !showPanel) {
      setOpenPanel(undefined)
    }
  }, [openPanel, setOpenPanel, showPanel])

  useEffect(() => {
    try {
      sendEvent('blob-size', {
        // for files over the size limit, lines will be 0
        lines: stylingDirectives?.length,
        // for files over the size limit, truncatedSloc will not be reported
        truncatedSloc: blob.headerInfo?.lineInfo.truncatedSloc,
        // for files over the size limit, truncatedLoc will not be reported
        truncatedLoc: blob.headerInfo?.lineInfo.truncatedLoc,
        // for files over the size limit length will be 0
        length: rawLines?.reduce((total, line) => total + line.length, 0) ?? 0,
        // for files over the size limit, humanLength will be reported
        humanLength: blob.headerInfo?.blobSize,
      })
    } catch {
      // don't want to crash the page just for analytics
    }
  }, [blob, stylingDirectives, rawLines])

  return (
    <CurrentLineRefMapProvider>
      {codeNavInfo && <FindSymbolShortcut codeNavInfo={codeNavInfo} />}
      <LatestCommitSingleLine />
      {bannerProps && (
        <div className="tmp-mb-3">
          <Banner {...bannerProps} />
        </div>
      )}
      <div className="d-flex flex-row">
        <div
          className={clsx(
            'container',
            styles.blobContainer,
            actualOpenPanel && codeNavInfo && screenSize > ScreenSize.medium && styles.blobContainerWithPanel,
          )}
        >
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <div className={clsx('react-code-size-details-banner', styles.codeSizeDetails)}>
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <CodeSizeDetails className="react-code-size-details-banner" />
            <CopilotPopover
              copilotInfo={copilotInfo}
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              className="react-code-size-details-banner"
              view={blame ? 'blame' : 'preview'}
            />
          </div>
          <div
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className={clsx('react-blob-view-header-sticky', styles.stickyHeader)}
            id={stickyHeaderId}
            ref={stickyHeaderRef}
          >
            <BlobViewHeader
              currentStickyLines={currentStickyLines}
              focusedSearchResult={focusedSearchResult}
              openPanel={actualOpenPanel}
              searchingText={searchingText}
              searchResults={searchResults}
              setFocusedSearchResult={setFocusedSearchResult}
              setOpenPanel={setOpenPanel}
              showTree={showTree}
              stickyHeaderElement={stickyHeaderElement}
              treeToggleElement={treeToggleElement}
              validCodeNav={validCodeNav}
              copilotInfo={copilotInfo}
              colorizedLines={colorizedLines}
              onBannerChange={setBannerProps}
            />
          </div>

          <div className={styles.blobContentWrapper}>
            <ErrorBoundary boundaryName="blob-content">
              <BlobContent
                blobLinesHandle={codeLinesHandleRef}
                setOpenPanel={setOpenPanel}
                validCodeNav={validCodeNav}
                codeNavInfo={codeNavInfo}
                onCodeNavTokenSelected={showCodeNavForToken}
                onLineStickOrUnstick={setStickyLines}
                searchResults={overlaySearchResults}
                focusedSearchResult={overlayFocusedSearchResult}
              />
            </ErrorBoundary>
          </div>
        </div>
        {actualOpenPanel && codeNavInfo ? (
          <>
            {/* This is a bit of a hack for SSR - we want the symbols panel to be shifted down when the
                Code size details are rendered above the blob (instead of in the blob header)
                Because that item needs to be inside the container above for the container query to work
                We need to shift the symbols panel ourselves when the content size is right */}
            <div className={styles.symbolsPanelSpacer} />
            <ErrorBoundary boundaryName="panel-content" fallback={null}>
              <PanelContent
                stickyStyle={stickyStyle}
                openPanel={actualOpenPanel}
                isCodeNavLoading={isCodeNavLoading}
                codeNavInfo={codeNavInfo}
                setOpenPanel={setOpenPanel}
                showCodeNavWithSymbol={showCodeNavWithSymbol}
                searchingText={searchingText}
                setSearchingText={setSearchingText}
                searchStatus={searchStatus}
                autoFocusSearch={autoFocusSearch}
              />
            </ErrorBoundary>
          </>
        ) : null}
      </div>
    </CurrentLineRefMapProvider>
  )
}

function FindSymbolShortcut({codeNavInfo}: {codeNavInfo: CodeNavigationInfo}) {
  const {sendRepoKeyDownEvent} = useReposAnalytics()
  const setOpenPanel = useSetOpenPanel()

  // Don't support `r` hotkey if no symbols exist for the document
  if (codeNavInfo.symbols.length === 0) {
    return null
  }

  return (
    <GlobalCommands
      commands={{
        'code-view:find-symbol': () => {
          setOpenPanel('codeNav')
          focusSymbolSearch()
          sendRepoKeyDownEvent('FIND_SYMBOL')
        },
      }}
    />
  )
}
